import { Filter, Download, Trash2, RotateCcw, Loader2, ChevronLeft, ChevronRight, MoreHorizontal, FileText, Send, BadgeCheck, Eye, CheckCircle2, ShoppingCart, BellRing, Pencil, Ban, AlertTriangle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { projectTypeLabels, statusLabels } from "./labels";
import { LeadEditDialog, LeadRejectDialog } from "./LeadDialogs";
import { LeadDetailDialog } from "./LeadDetailDialog";

export const LeadsTab = (p) => {
  const [editLead, setEditLead] = useState(null);
  const [rejectLead, setRejectLead] = useState(null);
  const [detailLead, setDetailLead] = useState(null);
  return (
  <>
    <LeadEditDialog lead={editLead} onClose={() => setEditLead(null)} onSaved={p.fetchData} API={p.API} />
    <LeadRejectDialog lead={rejectLead} onClose={() => setRejectLead(null)} onDone={p.fetchData} API={p.API} />
    <LeadDetailDialog lead={detailLead} onClose={() => setDetailLead(null)} API={p.API} />
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6" data-testid="leads-filters">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-700">Filtres :</span>
        </div>
        <Select value={p.filters.project_type} onValueChange={(value) => p.setFilters(prev => ({ ...prev, project_type: value === "all" ? "" : value }))}>
          <SelectTrigger className="w-[180px]" data-testid="filter-project-type"><SelectValue placeholder="Type de projet" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            {Object.entries(projectTypeLabels).map(([value, label]) => (<SelectItem key={value} value={value}>{label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Input placeholder="Filtrer par ville..." value={p.filters.city} onChange={(e) => p.setFilters(prev => ({ ...prev, city: e.target.value }))} className="w-[180px]" data-testid="filter-city" />
        <Select value={p.filters.status} onValueChange={(value) => p.setFilters(prev => ({ ...prev, status: value === "all" ? "" : value }))}>
          <SelectTrigger className="w-[180px]" data-testid="filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="to_validate">À valider</SelectItem>
            <SelectItem value="new">Nouveau</SelectItem>
            <SelectItem value="assigned">Vendu</SelectItem>
            <SelectItem value="contacted">Contacté</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="inactive">Inactif</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <Button variant={p.showTrash ? "default" : "outline"} size="sm" onClick={p.toggleTrashView} className={p.showTrash ? "bg-orange-500 hover:bg-orange-600 text-white" : "text-slate-600"} data-testid="toggle-trash-button">
            {p.showTrash ? (<><ArrowLeft className="w-4 h-4 mr-2" />Retour aux leads</>) : (<><Trash2 className="w-4 h-4 mr-2" />Corbeille ({p.trashLeads.length})</>)}
          </Button>
          <Button variant="outline" size="sm" onClick={() => p.handleExport("csv")} data-testid="export-csv-button"><Download className="w-4 h-4 mr-2" />CSV</Button>
          <Button variant="outline" size="sm" onClick={() => p.handleExport("excel")} data-testid="export-excel-button"><Download className="w-4 h-4 mr-2" />Excel</Button>
        </div>
      </div>
    </div>

    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="leads-table">
      {p.showTrash ? (
        p.trashLeads.length === 0 ? (
          <div className="text-center py-20" data-testid="trash-empty">
            <Trash2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-4">La corbeille est vide</p>
            <Button variant="outline" size="sm" onClick={p.toggleTrashView} className="text-slate-700" data-testid="trash-empty-back-button">
              <ArrowLeft className="w-4 h-4 mr-2" />Retour aux leads
            </Button>
          </div>
        ) : (
          <>
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm text-slate-600">Les leads sont conservés <strong>30 jours</strong> dans la corbeille puis supprimés définitivement.</span>
              <Button variant="outline" size="sm" onClick={p.toggleTrashView} className="text-slate-700 bg-white" data-testid="trash-back-button">
                <ArrowLeft className="w-4 h-4 mr-2" />Retour aux leads
              </Button>
            </div>
            {p.selectedLeads.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-orange-50 border-b border-orange-200" data-testid="trash-bulk-bar">
                <span className="text-sm font-semibold text-slate-900">{p.selectedLeads.length} lead(s) sélectionné(s)</span>
                <Button size="sm" variant="outline" onClick={p.handleBulkRestore} className="bg-white text-green-700 border-green-200 hover:bg-green-50" data-testid="bulk-restore-button">
                  <RotateCcw className="w-4 h-4 mr-1" />Restaurer
                </Button>
                <Button size="sm" variant="outline" onClick={p.handleBulkPurge} className="bg-white text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-testid="bulk-purge-button">
                  <Trash2 className="w-4 h-4 mr-1" />Supprimer définitivement
                </Button>
                <button onClick={() => p.setSelectedLeads([])} className="text-sm text-slate-500 hover:text-slate-700 ml-auto" data-testid="trash-clear-selection">Tout désélectionner</button>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox checked={p.trashLeads.length > 0 && p.selectedLeads.length === p.trashLeads.length} onCheckedChange={p.toggleTrashSelectAll} data-testid="select-all-trash" />
                  </TableHead>
                  <TableHead>N°</TableHead><TableHead>Contact</TableHead><TableHead>Projet</TableHead>
                  <TableHead>Localisation</TableHead><TableHead>Supprimé le</TableHead><TableHead>Expire dans</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {p.trashLeads.map((lead) => (
                  <TableRow key={lead.id} data-testid={`trash-row-${lead.id}`} className={p.selectedLeads.includes(lead.id) ? "bg-orange-50/50" : ""}>
                    <TableCell><Checkbox checked={p.selectedLeads.includes(lead.id)} onCheckedChange={() => p.toggleLeadSelection(lead.id)} data-testid={`select-trash-${lead.id}`} /></TableCell>
                    <TableCell><span className="font-mono text-xs text-orange-600 font-bold">{lead.lead_number || "—"}</span></TableCell>
                    <TableCell>
                      <p className="font-medium text-slate-900">{lead.first_name} {lead.last_name}</p>
                      <p className="text-sm text-slate-500">{lead.email}</p>
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">{projectTypeLabels[lead.project_type] || lead.project_type}</TableCell>
                    <TableCell><p className="text-slate-900">{lead.city}</p><p className="text-sm text-slate-500">{lead.postal_code}</p></TableCell>
                    <TableCell className="text-sm text-slate-500">{new Date(lead.deleted_at).toLocaleDateString("fr-BE")}</TableCell>
                    <TableCell><span className={`badge ${lead.days_left <= 7 ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>{lead.days_left} jour(s)</span></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => p.handleRestoreLead(lead.id)} className="text-green-700 hover:text-green-800 hover:bg-green-50" data-testid={`restore-lead-${lead.id}`}><RotateCcw className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => p.handlePurgeLead(lead.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`purge-lead-${lead.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )
      ) : p.loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
      ) : p.leads.length === 0 ? (
        <div className="text-center py-20"><p className="text-slate-500">Aucun lead trouvé</p></div>
      ) : (
        <>
          {p.selectedLeads.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-orange-50 border-b border-orange-200" data-testid="bulk-actions-bar">
              <span className="text-sm font-semibold text-slate-900">{p.selectedLeads.length} lead(s) sélectionné(s)</span>
              {p.leads.some(l => p.selectedLeads.includes(l.id) && l.published === false) && (
                <Button size="sm" onClick={p.handleBulkPublish} className="bg-green-600 hover:bg-green-700 text-white" data-testid="bulk-publish-button">
                  <Send className="w-4 h-4 mr-1" />Envoyer aux entreprises
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button size="sm" variant="outline" className="bg-white" data-testid="bulk-status-button">Changer le statut</Button></DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {Object.entries(statusLabels).map(([value, s]) => (
                    <DropdownMenuItem key={value} onClick={() => p.handleBulkStatus(value)} data-testid={`bulk-status-${value}`}>{s.label}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button size="sm" variant="outline" onClick={p.handleBulkDelete} className="bg-white text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-testid="bulk-delete-button">
                <Trash2 className="w-4 h-4 mr-1" />Supprimer
              </Button>
              <button onClick={() => p.setSelectedLeads([])} className="text-sm text-slate-500 hover:text-slate-700 ml-auto" data-testid="bulk-clear-selection">Tout désélectionner</button>
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"><Checkbox checked={p.leads.length > 0 && p.selectedLeads.length === p.leads.length} onCheckedChange={p.toggleSelectAll} data-testid="select-all-leads" /></TableHead>
                <TableHead>N°</TableHead><TableHead>Contact</TableHead><TableHead>Projet</TableHead><TableHead>Localisation</TableHead>
                <TableHead>Budget</TableHead><TableHead>Statut</TableHead><TableHead>Date</TableHead><TableHead>Fichiers</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {p.leads.map((lead) => (
                <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`} className={p.selectedLeads.includes(lead.id) ? "bg-orange-50/50" : ""}>
                  <TableCell><Checkbox checked={p.selectedLeads.includes(lead.id)} onCheckedChange={() => p.toggleLeadSelection(lead.id)} data-testid={`select-lead-${lead.id}`} /></TableCell>
                  <TableCell>
                    <button type="button" onClick={() => setDetailLead(lead)} className="font-mono text-xs text-orange-600 font-bold hover:underline hover:text-orange-700" title="Voir la fiche complète" data-testid={`lead-number-${lead.id}`}>
                      {lead.lead_number || "—"}
                    </button>
                    {lead.is_duplicate && (
                      <span className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-red-600" title={`Doublon détecté (${(lead.duplicate_by || []).map(m => m === "email" ? "e-mail" : "téléphone").join(", ")})`} data-testid={`lead-duplicate-${lead.id}`}>
                        <AlertTriangle className="w-3 h-3" />Doublon
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-slate-900">{lead.first_name} {lead.last_name}</p>
                      <p className="text-sm text-slate-500">{lead.email}</p>
                      <p className="text-sm text-slate-500">{lead.phone}</p>
                    </div>
                  </TableCell>
                  <TableCell><span className="font-medium text-slate-900">{projectTypeLabels[lead.project_type] || lead.project_type}</span></TableCell>
                  <TableCell><p className="text-slate-900">{lead.city}</p><p className="text-sm text-slate-500">{lead.postal_code}</p></TableCell>
                  <TableCell className="text-slate-600">{lead.budget}</TableCell>
                  <TableCell>
                    {lead.published === false ? (
                      <span className="badge bg-orange-100 text-orange-700 font-semibold" data-testid={`lead-to-validate-${lead.id}`}>À valider</span>
                    ) : (
                      <span className={`badge ${statusLabels[lead.status]?.class || "badge-pending"}`}>
                        {lead.status === "contacted" && <CheckCircle2 className="w-3 h-3 mr-1 inline text-green-600" data-testid={`status-contacted-check-${lead.id}`} />}
                        {statusLabels[lead.status]?.label || lead.status}
                      </span>
                    )}
                    {lead.published && lead.published_at && (
                      <p className="text-[11px] text-slate-400 mt-0.5" data-testid={`lead-published-${lead.id}`}>Envoyé le {new Date(lead.published_at).toLocaleDateString("fr-BE")}</p>
                    )}
                    {lead.buyers_count > 0 && (
                      <p className="flex items-center gap-1 text-xs text-orange-600 font-medium mt-1" data-testid={`lead-buyers-${lead.id}`}>
                        <ShoppingCart className="w-3 h-3" />Vendu à {lead.buyers_count} entreprise{lead.buyers_count > 1 ? "s" : ""}
                      </p>
                    )}
                    {lead.client_confirmed && (
                      <p className="flex items-center gap-1 text-xs text-green-600 font-medium mt-1" data-testid={`lead-confirmed-${lead.id}`}>
                        <BadgeCheck className="w-3 h-3" />Confirmé client
                      </p>
                    )}
                    {(lead.unsold_reminders?.["48h"] || lead.unsold_reminders?.["7d"]) && (
                      <p className="flex items-center gap-1 text-xs text-amber-600 mt-0.5" data-testid={`lead-reminders-${lead.id}`}>
                        <BellRing className="w-3 h-3" />Rappel {[lead.unsold_reminders?.["48h"] && "48h", lead.unsold_reminders?.["7d"] && "J+7"].filter(Boolean).join(" + ")} envoyé
                      </p>
                    )}
                    {lead.views_count > 0 && (
                      <p className="flex items-center gap-1 text-xs text-slate-400 mt-0.5" data-testid={`lead-views-${lead.id}`}>
                        <Eye className="w-3 h-3" />{lead.views_count} vue{lead.views_count > 1 ? "s" : ""} sans achat
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{new Date(lead.created_at).toLocaleDateString("fr-BE")}</TableCell>
                  <TableCell>
                    {lead.files && lead.files.length > 0 ? (
                      <div className="space-y-1">
                        {lead.files.map((f, i) => (
                          <a key={i} href={`${p.API}/leads/${lead.id}/files/${f}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                            <FileText className="w-3 h-3" />{f.split("_").slice(2).join("_")}
                          </a>
                        ))}
                        {!lead.files_forwarded && (
                          <Button size="sm" variant="ghost" className="text-xs text-green-600 h-6 px-2" onClick={() => p.handleForwardFiles(lead.id)} data-testid={`forward-files-${lead.id}`}>
                            <Send className="w-3 h-3 mr-1" /> Transférer
                          </Button>
                        )}
                        {lead.files_forwarded && <span className="text-xs text-green-600 font-medium">Transférés</span>}
                      </div>
                    ) : (<span className="text-xs text-slate-400">—</span>)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {lead.published === false && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setEditLead(lead)} className="h-8" title="Modifier avant envoi" data-testid={`edit-lead-btn-${lead.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" onClick={() => p.handlePublish(lead.id)} className="bg-green-600 hover:bg-green-700 text-white h-8" data-testid={`publish-lead-${lead.id}`}>
                            <Send className="w-3.5 h-3.5 mr-1" />Envoyer aux entreprises
                          </Button>
                        </>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" data-testid={`lead-actions-${lead.id}`}><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => p.handleStatusChange(lead.id, lead.status === "contacted" ? "new" : "contacted")} data-testid={`mark-contacted-${lead.id}`}>
                            {lead.status === "contacted" && <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" data-testid={`contacted-check-${lead.id}`} />}
                            {lead.status === "contacted" ? "Contacté" : "Marquer contacté"}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => p.handleStatusChange(lead.id, "inactive")} data-testid={`mark-inactive-${lead.id}`}>Marquer inactif</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setEditLead(lead)} data-testid={`edit-lead-${lead.id}`}><Pencil className="w-4 h-4 mr-2 text-slate-500" />Modifier le lead</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDetailLead(lead)} data-testid={`view-lead-${lead.id}`}><Eye className="w-4 h-4 mr-2 text-slate-500" />Voir la demande</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setRejectLead(lead)} className="text-red-600 focus:text-red-700" data-testid={`reject-lead-${lead.id}`}><Ban className="w-4 h-4 mr-2" />Refuser…</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button variant="ghost" size="sm" onClick={() => p.handleDeleteLead(lead.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-lead-${lead.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between px-4 py-4 border-t border-slate-200">
            <p className="text-sm text-slate-500">Page {p.currentPage} sur {p.totalPages}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => p.setCurrentPage(x => Math.max(1, x - 1))} disabled={p.currentPage === 1} data-testid="prev-page-button"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => p.setCurrentPage(x => Math.min(p.totalPages, x + 1))} disabled={p.currentPage === p.totalPages} data-testid="next-page-button"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        </>
      )}
    </div>
  </>
  );
};
